import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Search, Clock, X, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchHistory, setSearchHistory] = useState([]);
  const searchRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const cleHistorique = `crm_search_history_${user?.id || 'anon'}`;
  const estTechnicien = user?.role === 'technicien';

  useEffect(() => {
    const history = JSON.parse(localStorage.getItem(cleHistorique) || '[]');
    setSearchHistory(history.slice(0, 3));
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (query.length >= 1) {
        performSearch(query);
      } else {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (term) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/mes-prospects?search=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      const prospects = Array.isArray(data) ? data : (data.prospects || []);
      const mapped = prospects.slice(0, 6).map(p => ({
        id: p.id || p._id,
        nom: `${p.prenom || ''} ${p.nom || ''}`.trim(),
        telephone: p.numero_telephone || p.telephone || '',
        code_postal: p.code_postal || '',
        statut: estTechnicien ? null : (p.statut || 'Nouveau prospect'),
        initiales: `${(p.prenom || ' ')[0]}${(p.nom || ' ')[0]}`.toUpperCase()
      }));
      setResults(mapped);
    } catch (err) {
      setResults([]);
    }
    setIsLoading(false);
  };

  const saveToHistory = (prospect) => {
    const newHistory = [
      { id: prospect.id, nom: prospect.nom, telephone: prospect.telephone, statut: prospect.statut },
      ...searchHistory.filter(h => h.id !== prospect.id)
    ].slice(0, 3);
    setSearchHistory(newHistory);
    localStorage.setItem(cleHistorique, JSON.stringify(newHistory));
  };

  const handleSelect = (prospect) => {
    saveToHistory(prospect);
    setQuery('');
    setShowDropdown(false);
    navigate(estTechnicien ? '/ops/mes-chantiers' : `/prospects-entrants/${prospect.id}/fiche-rdv`, estTechnicien ? { state: { prospectId: prospect.id } } : undefined);
  };

  const handleHistoryClick = (item) => {
    navigate(estTechnicien ? '/ops/mes-chantiers' : `/prospects-entrants/${item.id}/fiche-rdv`, estTechnicien ? { state: { prospectId: item.id } } : undefined);
    setShowDropdown(false);
  };

  const handleKeyDown = (e) => {
    const list = query.length >= 1 ? results : [];
    if (!showDropdown || list.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, list.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, -1)); }
    if (e.key === 'Enter' && selectedIndex >= 0) handleSelect(list[selectedIndex]);
    if (e.key === 'Escape') { setShowDropdown(false); searchRef.current?.blur(); }
  };

  const getStatusStyle = (statut) => {
    const s = statut?.toLowerCase() || '';
    if (s.includes('nouveau')) return { bg: '#dbeafe', color: '#1d4ed8' };
    if (s.includes('rappeler')) return { bg: '#dcfce7', color: '#15803d' };
    if (s.includes('devis sign')) return { bg: '#f0fdf4', color: '#166534' };
    if (s.includes('devis')) return { bg: '#f3e8ff', color: '#7e22ce' };
    if (s.includes('install')) return { bg: '#ecfdf5', color: '#065f46' };
    if (s.includes('r\u00e9pond')) return { bg: '#fef9c3', color: '#854d0e' };
    return { bg: '#f1f5f9', color: '#475569' };
  };

  const highlight = (text, q) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <span>
        {text.slice(0, idx)}
        <span style={{color: '#3b82f6', fontWeight: 500}}>{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </span>
    );
  };

  const showHistory = showDropdown && query.length === 0 && searchHistory.length > 0;
  const showResults = showDropdown && query.length >= 1;

  return (
    <div ref={wrapRef} style={{position: 'relative', width: '100%', maxWidth: '360px'}}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        borderRadius: 100, padding: '9px 16px',
        background: 'rgba(255,255,255,0.95)',
        border: '0.5px solid rgba(227,232,238,1)',
        boxShadow: '0 1px 3px rgba(50,50,93,0.06)'
      }}>
        <Search style={{width: 14, height: 14, color: '#697386', opacity: 0.5, flexShrink: 0}} />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder="Rechercher un client, devis, facture…"
          className="pilot-search-input"
          style={{
            flex: 1, border: 'none', background: 'transparent',
            outline: 'none', fontSize: '12.5px', color: '#1A1F36'
          }}
        />
        <style>{`.pilot-search-input::placeholder { color: #697386; opacity: 1; }`}</style>
        {query ? (
          <button onClick={() => { setQuery(''); setResults([]); searchRef.current?.focus(); }}
            style={{background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0}}>
            <X style={{width: 14, height: 14, color: '#94a3b8'}} />
          </button>
        ) : isLoading ? (
          <div style={{width: 14, height: 14, border: '2px solid #e2e8f0', borderTopColor: '#1FD1FD', borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0}} />
        ) : null}
      </div>

      {(showHistory || showResults) && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 12,
          zIndex: 9999, overflow: 'hidden', minWidth: 360
        }}>
          {showHistory && (
            <div>
              <div style={{padding: '10px 14px 4px', fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                Recherches récentes
              </div>
              {searchHistory.map((item, i) => (
                <div key={item.id} onClick={() => handleHistoryClick(item)}
                  style={{display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', background: '#fff'}}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                  <div style={{width: 28, height: 28, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                    <Clock style={{width: 13, height: 13, color: '#64748b'}} />
                  </div>
                  <div style={{flex: 1}}>
                    <div style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>{item.nom}</div>
                    <div style={{fontSize: 11, color: '#64748b'}}>{item.telephone}</div>
                  </div>
                  <div style={{fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 10, background: getStatusStyle(item.statut).bg, color: getStatusStyle(item.statut).color, whiteSpace: 'nowrap'}}>
                    {item.statut}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showResults && results.length === 0 && !isLoading && (
            <div style={{padding: '20px 14px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>
              Aucun résultat pour "{query}"
            </div>
          )}

          {showResults && results.length > 0 && (
            <div>
              <div style={{padding: '10px 14px 4px', fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                Résultats
              </div>
              {results.map((r, i) => {
                const st = getStatusStyle(r.statut);
                return (
                  <div key={r.id} onClick={() => handleSelect(r)}
                    style={{display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', background: selectedIndex === i ? '#f0f9ff' : '#fff', borderLeft: selectedIndex === i ? '2px solid #3b82f6' : '2px solid transparent'}}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; setSelectedIndex(i); }}
                    onMouseLeave={e => { e.currentTarget.style.background = selectedIndex === i ? '#f0f9ff' : '#fff'; }}>
                    <div style={{width: 32, height: 32, borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 500, color: '#1d4ed8', flexShrink: 0}}>
                      {r.initiales}
                    </div>
                    <div style={{flex: 1, minWidth: 0}}>
                      <div style={{fontSize: 13, color: '#0f172a', fontWeight: 500}}>{highlight(r.nom, query)}</div>
                      <div style={{fontSize: 11, color: '#64748b'}}>{r.telephone}{r.code_postal ? ' · ' + r.code_postal : ''}</div>
                    </div>
                    <div style={{fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 10, background: st.bg, color: st.color, whiteSpace: 'nowrap', marginRight: 4}}>
                      {r.statut}
                    </div>
                    <ArrowRight style={{width: 14, height: 14, color: '#cbd5e1', flexShrink: 0}} />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SearchBar;
